import type {
  PassType,
  DashboardUIType,
  RevenueBreakdown,
  MemberSummaryData,
  DashboardInnerTabType,
  SalesTabType,
  MemberTabType,
} from "types/BusinessDashboard";
import { formatNumberToWonCurrency } from "utils/format";

export const businessDashboardTabOptions: {
  id: DashboardUIType;
  label: string;
}[] = [
  { id: "realtime", label: "실시간" },
  { id: "sales", label: "매출" },
  { id: "members", label: "유효회원" },
];

export const businessDashboardInnerTabOptions: {
  id: DashboardInnerTabType;
  label: string;
}[] = [
  { id: "day", label: "오늘" },
  { id: "week", label: "이번 주" },
  { id: "month", label: "이번 달" },
];

export const salesDashboardInnerTabOptions: {
  id: SalesTabType;
  label: string;
}[] = [
  { id: "daily", label: "일일 매출" },
  { id: "cumulative", label: "누적 매출" },
];

export const salesDashboardInnerMembersTabOptions: {
  id: MemberTabType;
  label: string;
}[] = [
  { id: "period", label: "기간제" },
  { id: "count", label: "횟수제" },
];

export const REALTIME_PASS_TYPE_MAP: {
  [key in PassType]: {
    key: string;
    label: string;
    color: string;
    lightColor: string;
    bgColor: string;
  };
} = {
  TOTAL: {
    key: "total",
    label: "총계",
    color: "#333333",
    lightColor: "#333333",
    bgColor: "#F3F4F6",
  },
  PERSONAL_LESSON: {
    key: "pt",
    label: "개인레슨",
    color: "#956AFF",
    lightColor: "#6E3CD6",
    bgColor: "#8F6DD514",
  },
  GROUP_CLASS: {
    key: "groupClass",
    label: "그룹수업",
    color: "#C27500",
    lightColor: "#F0A93C",
    bgColor: "#EDB85E14",
  },
  MEMBERSHIP: {
    key: "membership",
    label: "회원권",
    color: "rgb(0, 58, 972)",
    lightColor: "#1E65F0",
    bgColor: "#3B5CF014",
  },
  OPTION: {
    key: "option",
    label: "옵션",
    color: "#2E7A9F",
    lightColor: "#3CA5D8",
    bgColor: "#42A2D114",
  },
  ETC: {
    key: "etc",
    label: "기타",
    color: "#6C727F",
    lightColor: "#8A919E",
    bgColor: "#6C727F14",
  },
};

export const MEMBERS_PASS_TYPE_MAP: {
  [key: string]: {
    passType: PassType;
    apiKey: keyof MemberSummaryData;
    title: string;
    color: string;
    lightColor: string;
    bgColor: string;
  };
} = {
  total: {
    passType: "TOTAL",
    apiKey: "total",
    title: "전체",
    color: "#333333",
    lightColor: "#222222",
    bgColor: "#FFFFFF",
  },
  pt: {
    passType: "PERSONAL_LESSON",
    apiKey: "personalLesson",
    title: "개인",
    color: "#4E23A8",
    lightColor: "#6E3CD6",
    bgColor: "#F6F3FC",
  },
  groupClass: {
    passType: "GROUP_CLASS",
    apiKey: "groupClass",
    title: "그룹",
    color: "#C27500",
    lightColor: "#F0A93C",
    bgColor: "#FEF9F2",
  },
  membership: {
    passType: "MEMBERSHIP",
    apiKey: "membership",
    title: "회원권",
    color: "#003AAC",
    lightColor: "#1E65F0",
    bgColor: "#F3F5FE",
  },
  option: {
    passType: "OPTION",
    apiKey: "option",
    title: "옵션",
    color: "#2E7A9F",
    lightColor: "#3CA5D8",
    bgColor: "#F0F8FB",
  },
};

export const MEMBERS_LIGHT_BREAKDOWN_COLORS: { [key: string]: string } = {
  pt: "#6E3CD6",
  groupClass: "#F0A93C",
  membership: "#1E65F0",
  option: "#3CA5D8",
};

export const transformSalesDataForRevenueCard = (
  title: string,
  periodStr: string,
  salesData?: RevenueBreakdown
) => {
  if (!salesData) {
    return {
      title,
      period: periodStr,
      amount: "₩0",
      changeRate: { calculable: false, positive: null, value: null },
      category: "데이터 없음",
      categoryColor: "#6C727F",
      breakdown: [],
    };
  }

  const { total: totalMetric, ...breakdownItems } = salesData;
  const breakdownEntries = Object.entries(breakdownItems);

  let topCategoryKey: keyof typeof breakdownItems = "etc";
  if (breakdownEntries.length > 0) {
    const topItemEntry = breakdownEntries.reduce((max, current) => {
      const maxRevenue = max[1].revenue ?? -Infinity;
      const currentRevenue = current[1].revenue ?? -Infinity;
      return currentRevenue > maxRevenue ? current : max;
    });
    topCategoryKey = topItemEntry[0] as
      | "option"
      | "etc"
      | "personalLesson"
      | "groupClass"
      | "membership";
  }

  const keyToPassTypeMap: { [key in keyof typeof breakdownItems]: PassType } = {
    personalLesson: "PERSONAL_LESSON",
    groupClass: "GROUP_CLASS",
    membership: "MEMBERSHIP",
    option: "OPTION",
    etc: "ETC",
  };

  const topCategoryPassType = keyToPassTypeMap[topCategoryKey];
  const topCategoryInfo = REALTIME_PASS_TYPE_MAP[topCategoryPassType];

  const breakdown = breakdownEntries.map(([apiKey, metric]) => {
    const passType = keyToPassTypeMap[apiKey as keyof typeof keyToPassTypeMap];
    const uiInfo = REALTIME_PASS_TYPE_MAP[passType];

    return {
      name: uiInfo?.label || apiKey,
      amount: formatNumberToWonCurrency(metric.revenue),
      changeRate: metric.changeRate,
      color: uiInfo?.color || "#6C727F",
    };
  });

  return {
    title,
    period: periodStr,
    amount: formatNumberToWonCurrency(totalMetric?.revenue),
    changeRate: totalMetric?.changeRate,
    category: topCategoryInfo?.label || "기타",
    categoryColor: topCategoryInfo?.color || "#6C727F",
    breakdown,
  };
};

export const SALES_PASS_TYPE_MAP: {
  [key: string]: {
    passType: PassType;
    title: string;
    color: string;
    bgColor: string;
  };
} = {
  total: {
    passType: "TOTAL",
    title: "전체",
    color: "#333333",
    bgColor: "white",
  },
  pt: {
    passType: "PERSONAL_LESSON",
    title: "개인",
    color: "#4E23A8",
    bgColor: "#F6F3FC",
  },
  groupClass: {
    passType: "GROUP_CLASS",
    title: "그룹",
    color: "#C27500",
    bgColor: "#FEF9F2",
  },
  membership: {
    passType: "MEMBERSHIP",
    title: "회원권",
    color: "#003AAC",
    bgColor: "#F3F5FE",
  },
  option: {
    passType: "OPTION",
    title: "옵션",
    color: "#2E7A9F",
    bgColor: "#F0F8FB",
  },
  etc: {
    passType: "ETC",
    title: "기타",
    color: "#6C727F",
    bgColor: "bg-[#F4F5F6]",
  },
} as const;

export const getSalesApiPassType = (
  activeTab: keyof typeof SALES_PASS_TYPE_MAP
) => SALES_PASS_TYPE_MAP[activeTab].passType;

export const salesDashboardTabOptions = Object.entries(SALES_PASS_TYPE_MAP).map(
  ([id, { title }]) => ({ id, title })
);

export const membersDashboardTabOptions = Object.entries(
  MEMBERS_PASS_TYPE_MAP
).map(([id, { title }]) => ({
  id,
  title,
}));
